import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Chip } from "@/components/Chip";
import { FaceScanner } from "@/components/FaceScanner";
import { GeoCapture } from "@/components/GeoCapture";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { fmtFullDate, fmtTime, todayISO, monthOf, daysInMonth } from "@/lib/date";
import { toast } from "sonner";
import { ScanFace, LogIn, LogOut as LogOutIcon, MapPin, PartyPopper, Sun, ShieldAlert, Wrench } from "lucide-react";

const STAT = (label, value, color, testid) => (
  <div className="rounded-2xl p-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid={testid}>
    <div className="font-syne font-bold text-2xl" style={{ color }}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>{label}</div>
  </div>
);

export default function Home() {
  const { user, refresh } = useAuth();
  const { settings } = useSettings();

  const [today, setToday] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanMode, setScanMode] = useState("in"); // in|out
  const [geoOpen, setGeoOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);

  const todayStr = todayISO();
  const month = monthOf();
  const isSunday = new Date().getDay() === 0;
  const holidayToday = holidays.find((h) => h.date === todayStr);

  const load = async () => {
    try {
      const [att, hol, lv, us] = await Promise.all([
        api.get(`/attendance?month=${month}`),
        api.get("/holidays"),
        api.get("/leaves"),
        api.get("/users"),
      ]);
      const my = att.data.filter((a) => a.user_id === user.user_id);
      setMonthly(att.data);
      setHolidays(hol.data);
      setLeaves(lv.data);
      setAllUsers(us.data);
      setToday(my.find((a) => a.date === todayStr) || null);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const stats = useMemo(() => {
    const my = monthly.filter((a) => a.user_id === user?.user_id);
    const present = my.filter((a) => a.check_in && a.check_out).length;
    const half = my.filter((a) => a.check_in && !a.check_out).length;
    const totalDays = daysInMonth(month);
    const today = new Date();
    const [y, m] = month.split("-").map(Number);
    let workDays = 0;
    let sundays = 0;
    let holidayCount = 0;
    for (let d = 1; d <= totalDays; d++) {
      const dt = new Date(y, m - 1, d);
      if (dt > today) break;
      const dow = dt.getDay();
      const iso = `${month}-${String(d).padStart(2, "0")}`;
      const isHol = holidays.some((h) => h.date === iso);
      if (isHol) { holidayCount++; continue; }
      if (dow === 0 && (settings?.sundays_optional ?? true)) { sundays++; continue; }
      workDays++;
    }
    const myLeaves = leaves.filter((l) => l.user_id === user?.user_id && l.status === "approved" && l.date.startsWith(month)).length;
    const absent = Math.max(0, workDays - present - half - myLeaves);
    const pct = workDays ? Math.round(((present + 0.5 * half) / workDays) * 100) : 0;
    return { present, absent, half, leave: myLeaves, holiday: holidayCount, workDays, pct };
  }, [monthly, holidays, leaves, month, user, settings]);

  const startCheckIn = () => {
    if (!user.face_descriptor) {
      toast.error("Register your face first");
      setRegisterOpen(true);
      return;
    }
    setScanMode("in");
    setScanOpen(true);
  };
  const startCheckOut = () => {
    if (!user.face_descriptor) {
      toast.error("Register your face first");
      setRegisterOpen(true);
      return;
    }
    setScanMode("out");
    setScanOpen(true);
  };

  const onScanDone = () => {
    setScanOpen(false);
    setGeoOpen(true);
  };
  const onGeoConfirm = async (pos) => {
    setGeoOpen(false);
    try {
      const body = pos ? { lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy } : {};
      if (scanMode === "in") {
        await api.post("/attendance/check-in", body);
        toast.success("Checked in");
      } else {
        await api.post("/attendance/check-out", body);
        toast.success("Checked out");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const onRegisterDone = async ({ descriptors, simulated }) => {
    setRegisterOpen(false);
    if (simulated || !descriptors) { toast.message("Simulation registered"); return; }
    try {
      await api.post(`/users/${user.user_id}/face`, { descriptors });
      toast.success(`Face registered (${descriptors.length} samples)`);
      await refresh();
    } catch (e) {
      toast.error("Could not save face");
    }
  };

  return (
    <div className="px-4 pt-3 pb-4 space-y-4" data-testid="home-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>
          {fmtFullDate(todayStr)}
        </div>
        <div className="font-syne text-3xl font-bold tracking-tight" style={{ color: "#E8ECFF" }} data-testid="greeting">
          Hi, {user?.name?.split(" ")[0] || "there"}.
        </div>
      </div>

      {/* Check-in card */}
      <div className="rounded-3xl p-5 relative overflow-hidden" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid="check-card">
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full" style={{ background: "radial-gradient(circle, rgba(79,142,247,0.25), transparent 70%)" }} />

        <div className="flex items-center gap-2 mb-3">
          {holidayToday && <Chip color="purple" icon={<PartyPopper size={11} />}>{holidayToday.name}</Chip>}
          {isSunday && (settings?.sundays_optional ?? true) && <Chip color="gray" icon={<Sun size={11} />}>Sunday (optional)</Chip>}
          {!user?.face_descriptor && <Chip color="amber" icon={<ShieldAlert size={11} />}>Register face</Chip>}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Check-in</div>
            <div className="font-syne font-bold text-2xl" style={{ color: today?.check_in ? "#22D3A5" : "#7A85A3" }} data-testid="check-in-time">
              {today?.check_in ? fmtTime(today.check_in) : "— : —"}
            </div>
            {today?.check_in_lat && (
              <div className="text-[10px] flex items-center gap-1" style={{ color: "#7A85A3" }}>
                <MapPin size={10} /> {today.check_in_lat.toFixed(3)}, {today.check_in_lng.toFixed(3)}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Check-out</div>
            <div className="font-syne font-bold text-2xl" style={{ color: today?.check_out ? "#4F8EF7" : "#7A85A3" }} data-testid="check-out-time">
              {today?.check_out ? fmtTime(today.check_out) : "— : —"}
            </div>
          </div>
        </div>

        {!today?.check_in && (
          <button onClick={startCheckIn} data-testid="check-in-button"
            className="w-full h-12 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
            style={{ background: "#22D3A5", color: "#0A0D14" }}>
            <ScanFace size={18} /> Face Check-In
          </button>
        )}
        {today?.check_in && !today?.check_out && (
          <button onClick={startCheckOut} data-testid="check-out-button"
            className="w-full h-12 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
            style={{ background: "#F75A5A", color: "#fff" }}>
            <LogOutIcon size={18} /> Face Check-Out
          </button>
        )}
        {today?.check_in && today?.check_out && (
          <div className="rounded-xl px-4 py-3 text-center text-sm font-bold"
            style={{ background: "rgba(34,211,165,0.1)", border: "1px solid rgba(34,211,165,0.3)", color: "#22D3A5" }}
            data-testid="attendance-complete">
            ✓ Attendance complete for today
          </div>
        )}

        {!user?.face_descriptor && (
          <button onClick={() => setRegisterOpen(true)} data-testid="register-face-button"
            className="w-full mt-2 h-11 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
            style={{ background: "transparent", color: "#4F8EF7", border: "1px solid #4F8EF7" }}>
            <LogIn size={16} /> Register your face
          </button>
        )}
      </div>

      {/* Monthly stats */}
      <div>
        <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "#7A85A3" }}>This Month</div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {STAT("Present", stats.present, "#22D3A5", "stat-present")}
          {STAT("Absent", stats.absent, "#F75A5A", "stat-absent")}
          {STAT("Half Day", stats.half, "#F7C94F", "stat-half")}
          {STAT("Leave", stats.leave, "#4F8EF7", "stat-leave")}
          {STAT("Holiday", stats.holiday, "#9B6FEB", "stat-holiday")}
          {STAT("Work Days", stats.workDays, "#E8ECFF", "stat-work")}
        </div>
        <div className="rounded-2xl p-4" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
          <div className="flex justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Attendance</div>
            <div className="font-syne font-bold" style={{ color: stats.pct >= 90 ? "#22D3A5" : stats.pct >= 75 ? "#F7C94F" : "#F75A5A" }} data-testid="attendance-pct">{stats.pct}%</div>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "#1E2640" }}>
            <div className="h-full" style={{ width: `${stats.pct}%`, background: stats.pct >= 90 ? "#22D3A5" : stats.pct >= 75 ? "#F7C94F" : "#F75A5A" }} />
          </div>
        </div>
      </div>

      {/* Kiosk shortcut for admin / HR Manager */}
      {user?.role === "admin" || user?.role === "hr_manager" ? (
        <Link
          to="/kiosk"
          data-testid="kiosk-shortcut"
          className="block rounded-2xl p-4 active:scale-[0.99] transition-transform"
          style={{ background: "linear-gradient(135deg, rgba(34,211,165,0.15), rgba(79,142,247,0.15))", border: "1px solid rgba(34,211,165,0.3)" }}
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "#22D3A5", color: "#0A0D14" }}>
              <ScanFace size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-syne font-bold tracking-tight" style={{ color: "#E8ECFF" }}>Kiosk Scan</div>
              <div className="text-xs" style={{ color: "#7A85A3" }}>One camera, auto check-in everyone</div>
            </div>
            <Chip color="green">HR</Chip>
          </div>
        </Link>
      ) : null}

      {/* Admin team overview */}
      {user?.role === "admin" && (
        <div data-testid="team-overview">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Today's Team</div>
            <div className="flex gap-2">
              <Chip color="green">{monthly.filter((a) => a.date === todayStr && a.check_in).length} Present</Chip>
              <Chip color="red">{allUsers.length - monthly.filter((a) => a.date === todayStr && a.check_in).length} Absent</Chip>
            </div>
          </div>
          <div className="space-y-2">
            {allUsers.map((u) => {
              const att = monthly.find((a) => a.user_id === u.user_id && a.date === todayStr);
              const status = att?.check_in && att?.check_out ? "PRESENT" : att?.check_in ? "PARTIAL" : "ABSENT";
              const color = status === "PRESENT" ? "green" : status === "PARTIAL" ? "amber" : "red";
              return (
                <div key={u.user_id} className="flex items-center gap-3 rounded-2xl p-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid={`team-row-${u.user_id}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold font-syne" style={{ background: "#1E2640", color: "#E8ECFF" }}>
                    {u.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate" style={{ color: "#E8ECFF" }}>{u.name}</div>
                    <div className="text-xs truncate" style={{ color: "#7A85A3" }}>{u.designation || u.role}</div>
                  </div>
                  <div className="text-right">
                    <Chip color={color}>{status}</Chip>
                    {att?.check_in && <div className="text-[10px] mt-1 font-mono" style={{ color: "#7A85A3" }}>{fmtTime(att.check_in)}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <FaceScanner
        open={scanOpen}
        mode="verify"
        referenceDescriptor={user?.face_descriptor}
        title={scanMode === "in" ? "Face Check-In" : "Face Check-Out"}
        onDone={onScanDone}
        onClose={() => setScanOpen(false)}
      />
      <FaceScanner
        open={registerOpen}
        mode="register"
        title="Register Your Face"
        onDone={onRegisterDone}
        onClose={() => setRegisterOpen(false)}
      />
      <GeoCapture open={geoOpen} mode={scanMode} onConfirm={onGeoConfirm} onClose={() => setGeoOpen(false)} />
    </div>
  );
}
